import { yupResolver } from "@hookform/resolvers/yup";
import { iArea } from "core/kangarooTypes/iArea";
import { iCity } from "core/kangarooTypes/iCity";
import { iPackageSize } from "core/kangarooTypes/iPackageSize";
import { iOrderToAdd } from "core/kangarooTypes/OrderToAdd";
import { iAddresses } from "core/kangarooTypes/ProfileManagement";
import {
  getAreas,
  getCites,
  getPackagesSize,
} from "core/Services/CommonServices";
import { addOrder, getMerchant } from "core/Services/MerchantServices";
import {
  getInventoryDashboard,
  DashboardInventoryItem,
} from "core/Services/InventoryServices";

import {
  ChevronDown,
  PanelTopClose,
  Plus,
  Send,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { toast } from "react-toastify";
import { USER_ID } from "utils/constant";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

const OrderForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const OrderFormSchema = yup.object().shape({
    customer_name: yup
      .string()
      .required(t("orderForm.validation.customerNameRequired")),
    customer_phone: yup
      .string()
      .required(t("orderForm.validation.phoneNumberRequired")),
    payment_method: yup
      .string()
      .required(t("orderForm.validation.paymentMethodRequired")),
    price: yup
      .number()
      .nullable()
      .transform((value) => (isNaN(value) ? null : value))
      .when("payment_method", {
        is: "COD",
        then: (schema) =>
          schema.required(t("orderForm.validation.orderAmountRequired")),
        otherwise: (schema) => schema.nullable().default(0),
      }),
    items: yup
      .array()
      .of(
        yup.object().shape({
          product_sku: yup
            .string()
            .required(t("orderForm.validation.productRequired")),
          quantity: yup
            .number()
            .required(t("orderForm.validation.quantityRequired"))
            .min(1, t("orderForm.validation.quantityMin")),
        })
      )
      .min(1, t("orderForm.validation.itemsRequired")),
    phone: yup.string(),
  });
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<iOrderToAdd>({
    resolver: yupResolver(OrderFormSchema as any),
    defaultValues: {
      payment_method: "",
      customer_name: "",
      customer_phone: "",
      price: null,
      items: [{ product_sku: "", quantity: 1 }],
      phone: "+962",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const phones = [
    { value: "+962", label: "+962 Jordan" },
    { value: "+966", label: "+966 Saudi Arabia" },
    { value: "+971", label: "+971 United Arab Emirates" },
    { value: "+965", label: "+965 Kuwait" },
    { value: "+973", label: "+973 Bahrain" },
    { value: "+974", label: "+974 Qatar" },
    { value: "+968", label: "+968 Oman" },
    { value: "+964", label: "+964 Iraq" },
    { value: "+963", label: "+963 Syria" },
    { value: "+961", label: "+961 Lebanon" },
    { value: "+970", label: "+970 Palestine" },
    { value: "+20", label: "+20 Egypt" },
    { value: "+90", label: "+90 Turkey" },
    { value: "+98", label: "+98 Iran" },
    { value: "+967", label: "+967 Yemen" },
    { value: "+249", label: "+249 Sudan" },
  ];

  const [cities, setCities] = useState<iCity[]>([]);
  const [areas, setAreas] = useState<iArea[]>([]);
  const [packageSizes, setPackageSizes] = useState<iPackageSize[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<iAddresses[]>([]);
  const [inventoryProducts, setInventoryProducts] = useState<
    DashboardInventoryItem[]
  >([]);
  const [loadingInventory, setLoadingInventory] = useState<boolean>(false);
  const [stockWarnings, setStockWarnings] = useState<{ [key: number]: string }>(
    {}
  );
  const [currentMerchantId, setCurrentMerchantId] = useState<string | null>(
    null
  );
  const [merchantInfo, setMerchantInfo] = useState<any>(null);
  const [isAdminMode, setIsAdminMode] = useState<boolean>(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      // Get merchant ID from URL parameter (for admin) or localStorage (for merchant)
      const merchantIdFromUrl = searchParams.get("merchantId");
      const userId = localStorage.getItem(USER_ID);
      const merchantId = merchantIdFromUrl || userId;

      // Check if admin is creating order for a merchant
      setIsAdminMode(!!merchantIdFromUrl && merchantIdFromUrl !== userId);

      setCurrentMerchantId(merchantId); // Store merchant ID
      const [citiesResponse, areasResponse, packageSizesResponse, merchant] =
        await Promise.all([
          getCites(),
          getAreas(),
          getPackagesSize(),
          getMerchant(merchantId!),
        ]);
      setCities(citiesResponse.data);
      setAreas(areasResponse.data);
      setPackageSizes(packageSizesResponse.data);
      setAddresses(merchant.data.addresses);

      // Store merchant info for display
      if (merchantIdFromUrl && merchantIdFromUrl !== userId) {
        setMerchantInfo({
          name: `${merchant.data.first_name} ${merchant.data.last_name}`,
          businessName: merchant.data.business_name,
          email: merchant.data.email,
        });
      }
    } catch (error) {
      toast.error("Failed to fetch data.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInventoryProducts = async () => {
    try {
      setLoadingInventory(true);
      const response = await getInventoryDashboard(
        currentMerchantId ? Number(currentMerchantId) : 0,
        {
          per_page: 500,
          // Don't filter by min_quantity here - we'll filter in UI to show disabled options
        },
        1
      );
      console.log("Fetched inventory products:", response.data); // Debug log

      // Filter out products with 0 quantity for selection, but keep them visible as disabled
      const allProducts = response.data || [];
      setInventoryProducts(allProducts);

      const productsWithStock = allProducts.filter(
        (p: any) => p.total_quantity > 0
      );

      if (productsWithStock.length === 0) {
        toast.info(
          "No products available with stock. Please add inventory first."
        );
      }
    } catch (error: any) {
      console.error("Error fetching inventory:", error);
      toast.error(
        error?.response?.data?.message || "Failed to fetch inventory products."
      );
    } finally {
      setLoadingInventory(false);
    }
  };

  // Real-time quantity validation for fulfillment orders
  const validateItemQuantity = (
    itemIndex: number,
    quantity: number,
    productSku: string
  ) => {
    const product = inventoryProducts.find((p) => p.sku === productSku);
    const newWarnings = { ...stockWarnings };

    if (!product) {
      delete newWarnings[itemIndex];
      setStockWarnings(newWarnings);
      return;
    }

    // Check if product is selected multiple times
    const allItems = watch("items");
    const duplicateCount = allItems.filter(
      (item: any) => item.product_sku && item.product_sku === productSku
    ).length;

    if (duplicateCount > 1) {
      // Calculate total quantity for this product across all items
      const totalQuantityForProduct = allItems.reduce(
        (sum: number, item: any) => {
          if (item.product_sku && item.product_sku === productSku) {
            return sum + (Number(item.quantity) || 0);
          }
          return sum;
        },
        0
      );

      if (totalQuantityForProduct > product.total_quantity) {
        newWarnings[
          itemIndex
        ] = `This product is selected ${duplicateCount} times! Total quantity (${totalQuantityForProduct}) exceeds available stock (${product.total_quantity})`;
      } else {
        newWarnings[
          itemIndex
        ] = `Warning: This product is selected ${duplicateCount} times. Total quantity: ${totalQuantityForProduct}/${product.total_quantity}`;
      }
    } else if (quantity > product.total_quantity) {
      newWarnings[
        itemIndex
      ] = `Insufficient stock! Available: ${product.total_quantity}`;
    } else {
      delete newWarnings[itemIndex];
    }

    setStockWarnings(newWarnings);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const cityId = watch("city_id");
  const areaId = watch("area_id");

  // Watch items in real-time for price calculation
  const items = useWatch({
    control,
    name: "items",
  });

  // Fetch inventory when merchant ID becomes available
  useEffect(() => {
    if (currentMerchantId) {
      fetchInventoryProducts();
    }
  }, [currentMerchantId]);

  // Update effect to handle both cases: all areas or filtered by city
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        setIsLoading(true);
        const areasResponse = await getAreas(
          cityId ? Number(cityId) : undefined
        );
        setAreas(areasResponse.data);
        // Reset area selection when city changes
        setValue("area_id", null);
      } catch (error) {
        toast.error("Failed to fetch areas.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAreas();
  }, [cityId, setValue]);

  useEffect(() => {
    const city = cities?.find((city) => city.id === Number(cityId));
    const area = areas?.find((area) => area.id === Number(areaId));
  }, [cityId, areaId, cities, areas, packageSizes, setValue]);

  // Calculate order amount based on selected products
  useEffect(() => {
    if (items) {
      let totalAmount = 0;

      items.forEach((item: any) => {
        if (item.product_sku && item.quantity) {
          const product = inventoryProducts.find(
            (p) => p.sku === item.product_sku
          );
          if (product) {
            totalAmount += product.price * Number(item.quantity);
          }
        }
      });

      setValue("price", totalAmount > 0 ? totalAmount : null);
    }
  }, [items, inventoryProducts, setValue]);

  // Stock validation function
  const validateStock = (items: any[]): boolean => {
    // Aggregate quantities by product_sku
    const productQuantities: { [key: string]: number } = {};
    items.forEach((item) => {
      if (item.product_sku && item.quantity) {
        const sku = item.product_sku;
        productQuantities[sku] =
          (productQuantities[sku] || 0) + Number(item.quantity);
      }
    });

    // Check stock for each unique product
    let allValid = true;
    Object.entries(productQuantities).forEach(([sku, totalQuantity]) => {
      const product = inventoryProducts.find((p) => p.sku === sku);
      if (!product || totalQuantity > product.total_quantity) {
        allValid = false;
      }
    });

    return allValid;
  };

  const onSubmit = async (data: iOrderToAdd) => {
    // Check for stock warnings
    const hasStockErrors = Object.values(stockWarnings).some((warning) =>
      warning.includes("exceeds")
    );

    if (hasStockErrors) {
      toast.error(
        "Cannot submit order: Total quantity exceeds available stock for some products. Please adjust quantities."
      );
      return;
    }

    // Validate stock
    const stockValid = validateStock(data.items);
    if (!stockValid) {
      toast.error(
        "Some products have insufficient stock. Please check and adjust quantities."
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const merchantIdFromUrl = searchParams.get("merchantId");
      const loggedInUserId = localStorage.getItem(USER_ID);

      const formData: any = {
        type: "fulfillment",
        customer_name: data.customer_name,
        customer_phone: `${data.phone}${data.customer_phone}`,
        payment_method: data.payment_method,
        price: data.price || 0,
      };

      // Add merchant_id if admin is creating order
      if (merchantIdFromUrl && merchantIdFromUrl !== loggedInUserId) {
        formData.merchant_id = parseInt(merchantIdFromUrl);
      }

      // Add items (simple - just sku and quantity)
      formData.items = data.items.map((item: any) => ({
        product_sku: item.product_sku,
        quantity: item.quantity,
      }));

      await addOrder(formData);
      toast.success("Order added successfully!");
      handleClearForm();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to add order. Please try again."
      );
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddMoreItems = () => {
    append({ product_sku: "", quantity: 1 } as any);
  };

  const handleClearForm = () => {
    reset();
    // If admin is creating order for merchant, navigate back to merchant's dashboard
    if (isAdminMode && currentMerchantId) {
      navigate(`/admin-merchant-dashboard/${currentMerchantId}`);
    } else {
      navigate("/merchant-dashboard?pageTitle=Dashboard");
    }
  };

  const renderPackageSizeOptions = () => {
    return (
      <div className="w-full hidden">
        {isLoading ? (
          // Show skeletons while loading
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array(3)
              .fill(0)
              .map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="border rounded-lg p-4 w-full"
                >
                  <div className="flex justify-between w-full">
                    <Skeleton width={50} height={20} />
                    <Skeleton width={50} height={20} />
                  </div>
                  <Skeleton width={200} height={16} className="mt-2" />
                </div>
              ))}
          </div>
        ) : packageSizes?.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {packageSizes.map((pkg) => (
                <div
                  key={pkg.id}
                  className="border rounded-lg p-4 cursor-pointer"
                >
                  <div className="flex justify-between">
                    <span className="font-medium">{pkg.name}</span>
                    <div className="text-right">
                      {pkg.old_price && pkg.discount ? (
                        <>
                          <span className="text-sm line-through text-gray-500">
                            {pkg.old_price}
                          </span>
                          <span className="text-sm text-green-600 ml-1">
                            -{pkg.discount}%
                          </span>
                        </>
                      ) : null}{" "}
                      <div
                        className={`text-sm ${
                          pkg.price === 0 ? "text-green-800 font-bold" : ""
                        }`}
                      >
                        {pkg.price === 0
                          ? "Free of charge"
                          : `JOD ${pkg.price}`}
                      </div>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 mt-2">
                    {pkg.description}
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-gray-500">No package sizes available</div>
        )}
      </div>
    );
  };

  return (
    <div className="pb-11">
      {/* Header Section */}
      <div className="sticky top-0 z-20 w-full flex flex-col sm:flex-row gap-3 justify-end bg-white px-4 sm:px-6 py-4 shadow-md">
        <div className="flex w-full sm:w-auto justify-end gap-3">
          <Button
            onClick={handleClearForm}
            icon={<PanelTopClose size={16} />}
            variant="secondary"
            className="w-full sm:w-auto"
          >
            {t("orderForm.close")}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            icon={<Send size={16} />}
            isLoading={isSubmitting}
            className="w-full sm:w-auto"
          >
            {t("orderForm.submitOrder")}
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-6 h-full items-center px-4 sm:px-6 py-4">
        {/* Admin Mode Banner - Show when admin is creating order for merchant */}
        {isAdminMode && merchantInfo && (
          <div className="w-full bg-blue-50 border-2 border-blue-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 text-white rounded-full p-2">
                <AlertCircle size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">
                  Creating Order for Merchant
                </h3>
                <p className="text-sm text-blue-700">
                  <span className="font-medium">
                    {merchantInfo.businessName}
                  </span>
                  {" - "}
                  {merchantInfo.name}
                  {" • "}
                  {merchantInfo.email}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Customer Information Section */}
        <div className="w-full bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <span className="text-lg font-semibold mb-4 block">
            {t("orderForm.customerInformation")}
          </span>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <div className="w-full sm:w-1/2">
                <FormInput
                  label={t("orderForm.customerName")}
                  name="customer_name"
                  register={register}
                  errors={errors}
                />
              </div>

              <div className="w-full sm:w-1/2 flex flex-col sm:flex-row gap-2">
                <div className="w-full sm:w-1/4">
                  <FormInput
                    name="phone"
                    label={t("orderForm.phone")}
                    type="select"
                    register={register}
                    errors={errors}
                    selectOptions={phones}
                    icon={<ChevronDown size={20} />}
                  />
                </div>
                <div className="w-full sm:w-3/4">
                  <FormInput
                    label={t("orderForm.phoneNumber")}
                    name="customer_phone"
                    type="text"
                    register={register}
                    errors={errors}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Order Details Section */}
        <div className="w-full bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <span className="text-lg font-semibold mb-4 block">
            {t("orderForm.orderDetails")}
          </span>

          <div className="flex flex-col gap-4">
            {/* Items Section - Fulfillment Only */}
            <div className="flex flex-col gap-4 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-base font-semibold text-gray-800">
                  {t("orderForm.orderItems")}
                </span>
                <span className="text-sm text-gray-500">
                  {fields.length} {fields.length === 1 ? "item" : "items"}
                </span>
              </div>

              {fields.map((field, index) => {
                const selectedProductSku = watch(`items.${index}.product_sku`);
                const selectedQuantity = watch(`items.${index}.quantity`);
                const selectedProduct = inventoryProducts.find(
                  (p) => p.sku === selectedProductSku
                );

                // Get all selected product SKUs from other items (excluding current)
                const allItems = watch("items");
                const otherSelectedProductSkus = allItems
                  .map((item: any, idx: number) => {
                    if (idx !== index && item.product_sku) {
                      return item.product_sku;
                    }
                    return null;
                  })
                  .filter((sku: string | null) => sku !== null);

                return (
                  <div
                    key={field.id}
                    className="relative flex flex-col gap-3 p-5 rounded-xl border-2 transition-all duration-200 bg-gradient-to-br from-blue-50 via-white to-blue-50 border-blue-200 hover:border-blue-300 hover:shadow-md"
                  >
                    {/* Item Number Badge */}
                    <div className="absolute -top-3 -left-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm shadow-lg">
                      {index + 1}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 w-full items-start">
                      {/* Fulfillment: Product Selector */}
                      <div className="w-full sm:w-2/3">
                        <label className="block text-sm font-semibold mb-2 text-gray-700">
                          {t("orderForm.product")}
                        </label>
                        <select
                          {...register(`items.${index}.product_sku`, {
                            onChange: (e) => {
                              const productSku = e.target.value;
                              const quantity =
                                watch(`items.${index}.quantity`) || 1;
                              if (productSku && quantity) {
                                validateItemQuantity(
                                  index,
                                  quantity,
                                  productSku
                                );
                              }
                              // Re-validate all items to update warnings
                              allItems.forEach((item: any, idx: number) => {
                                if (item.product_sku && item.quantity) {
                                  validateItemQuantity(
                                    idx,
                                    Number(item.quantity),
                                    item.product_sku
                                  );
                                }
                              });
                            },
                          })}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all duration-200 text-sm"
                        >
                          <option value="">
                            🔍 Select a product from inventory
                          </option>
                          {loadingInventory ? (
                            <option disabled>⏳ Loading products...</option>
                          ) : inventoryProducts.length === 0 ? (
                            <option disabled>📦 No products available</option>
                          ) : (
                            inventoryProducts
                              .filter((product) => product.total_quantity > 0)
                              .map((product) => {
                                const isAlreadySelected =
                                  otherSelectedProductSkus.includes(
                                    product.sku
                                  );
                                return (
                                  <option
                                    key={product.sku}
                                    value={product.sku}
                                    disabled={isAlreadySelected}
                                  >
                                    {product.product_name} | Available:{" "}
                                    {product.total_quantity} | JOD{" "}
                                    {product.price}
                                    {isAlreadySelected
                                      ? " ✓ (Already Selected)"
                                      : ""}
                                  </option>
                                );
                              })
                          )}
                        </select>

                        {/* Enhanced Selected Product Info Card */}
                        {selectedProduct && (
                          <div className="mt-3 p-4 bg-white rounded-lg border-2 border-blue-200 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-bold text-base text-gray-800">
                                {selectedProduct.product_name}
                              </h4>
                              <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                SKU: {selectedProduct.sku}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                <p className="text-xs text-green-700 font-medium mb-1">
                                  📦 Available Stock
                                </p>
                                <p className="text-2xl font-bold text-green-800">
                                  {selectedProduct.total_quantity}
                                </p>
                              </div>

                              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                <p className="text-xs text-blue-700 font-medium mb-1">
                                  💰 Unit Price
                                </p>
                                <p className="text-2xl font-bold text-blue-800">
                                  JOD {selectedProduct.price}
                                </p>
                              </div>
                            </div>

                            {/* Subtotal Calculation */}
                            {selectedQuantity && selectedQuantity > 0 && (
                              <div className="mt-3 pt-3 border-t-2 border-gray-200">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-600">
                                    Subtotal ({selectedQuantity} × JOD{" "}
                                    {selectedProduct.price}):
                                  </span>
                                  <span className="text-lg font-bold text-blue-600">
                                    JOD{" "}
                                    {(
                                      selectedQuantity * selectedProduct.price
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {errors.items?.[index]?.product_sku && (
                          <span className="text-red-500 text-xs mt-2 block font-medium">
                            ⚠️ Product is required
                          </span>
                        )}
                      </div>

                      {/* Quantity Input */}
                      <div className="w-full sm:w-1/4 flex flex-col gap-2">
                        <label className="block text-sm font-semibold mb-1 text-gray-700">
                          {t("orderForm.quantity")}
                        </label>
                        <input
                          type="number"
                          min="1"
                          max={
                            selectedProduct
                              ? selectedProduct.total_quantity
                              : undefined
                          }
                          {...register(`items.${index}.quantity`, {
                            onChange: (e) => {
                              if (selectedProductSku) {
                                validateItemQuantity(
                                  index,
                                  Number(e.target.value),
                                  selectedProductSku
                                );
                                // Re-validate all items to update total warnings
                                const allItems = watch("items");
                                allItems.forEach((item: any, idx: number) => {
                                  if (
                                    item.product_sku &&
                                    item.quantity &&
                                    idx !== index
                                  ) {
                                    validateItemQuantity(
                                      idx,
                                      Number(item.quantity),
                                      item.product_sku
                                    );
                                  }
                                });
                              }
                            },
                          })}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-bold text-lg transition-all duration-200"
                        />
                        {errors.items?.[index]?.quantity && (
                          <span className="text-red-500 text-xs mt-1 font-medium">
                            ⚠️ {errors.items[index]?.quantity?.message}
                          </span>
                        )}

                        {/* Show quantity warning */}
                        {selectedProduct &&
                          selectedQuantity > selectedProduct.total_quantity && (
                            <span className="text-orange-600 text-xs mt-1 font-medium">
                              ⚠️ Max: {selectedProduct.total_quantity}
                            </span>
                          )}
                      </div>

                      {/* Remove Button */}
                      {fields.length > 1 && (
                        <div className="flex items-end pb-2">
                          <button
                            type="button"
                            onClick={() => {
                              remove(index);
                              // Clear stock warning for this index
                              const newWarnings = { ...stockWarnings };
                              delete newWarnings[index];
                              setStockWarnings(newWarnings);
                            }}
                            className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-all duration-200 hover:scale-110"
                            title="Remove item"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Stock Warning */}
                    {stockWarnings[index] && (
                      <div
                        className={`flex items-start gap-3 text-sm mt-2 p-4 rounded-lg border-2 ${
                          stockWarnings[index].includes("exceeds")
                            ? "bg-red-50 text-red-800 border-red-300"
                            : "bg-yellow-50 text-yellow-800 border-yellow-300"
                        }`}
                      >
                        <AlertCircle
                          size={20}
                          className="flex-shrink-0 mt-0.5"
                        />
                        <span className="font-medium leading-relaxed">
                          {stockWarnings[index]}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              <Button
                onClick={() => append({ product_sku: "", quantity: 1 } as any)}
                icon={<Plus size={16} />}
                variant="outlined"
                className="text-blue-600 w-full sm:w-fit border-blue-600 hover:bg-blue-50 font-semibold"
              >
                {t("orderForm.addMoreItems")}
              </Button>
            </div>
          </div>
        </div>

        {/* Payment Information Section */}
        <div className="w-full bg-white p-4 sm:p-6 rounded-lg shadow-md flex flex-col gap-2">
          <span className="text-lg font-semibold">
            {t("orderForm.paymentInformation")}
          </span>

          <div className="flex flex-col gap-2">
            <span className="block text-sm font-medium">
              {t("orderForm.paymentMethod")}
            </span>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="prepaid"
                  value="prepaid"
                  {...register("payment_method")}
                  className="me-2"
                />
                <label htmlFor="prepaid">{t("orderForm.prepaid")}</label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="cod"
                  value="COD"
                  {...register("payment_method")}
                  className="me-2"
                />
                <label htmlFor="cod">{t("orderForm.cashOnDelivery")}</label>
              </div>
            </div>
            {errors["payment_method"] && (
              <p className="text-red-500 text-xs mt-1">
                {errors["payment_method"]?.message?.toString()}
              </p>
            )}
            <div className="w-full sm:w-1/2">
              <FormInput
                type="number"
                label={t("orderForm.orderAmount")}
                name="price"
                register={register}
                errors={errors}
                disabled={true}
              />
              <span className="text-xs text-gray-500 mt-1">
                {t("orderForm.autoCalculated")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderForm;
